import type { CoverageRow, WeekEntry } from "./coverage";
import type { Zone } from "./coverageVocabulary";

/* Turning what the database returns into what the coverage rules take.
 *
 * ── WHY THIS IS ITS OWN MODULE ────────────────────────────────────────────────────────────
 *
 * It was inline in `ClientCoverage.tsx` and had to be shared the moment a second screen — the
 * goal-gap report — needed the same week. Two copies of a shape conversion is the shape B-4
 * session 2 paid for: **a screen's only untested code is usually a `.map()`, and that is exactly
 * where a silent-blank defect lives.** A PostgREST embed comes back as an object or an array
 * depending on the relationship it infers, `.name` on an array is `undefined`, and a whole column
 * reads "Untitled" with nothing going red.
 *
 * So the CONVERSIONS live here, pure and executed by `coverageWeek.test.ts`, and the awaiting of
 * queries lives in `routes/useCoverageWeek.ts`, which the untested-rule ratchet deliberately does
 * not cover because there is nothing pure in it to cover.
 *
 * ── 🔴 "THE LIBRARY" MEANS THE WHOLE DESCRIBED CATALOGUE, NOT THIS WEEK'S EXERCISES ────────
 *
 * `ClientCoverage` originally read `exercise_coverage` filtered to the week's own exercises, which
 * was right when the only described exercises WERE one athlete's week. §11.3 measured what that
 * costs: the audit set could only ever report a cell that is servable AND empty, and with the
 * library equal to the week that intersection is empty **by construction** — so "0 cells nothing
 * trains" read exactly like a clean audit. §12.6 closed it by describing all 108 coach exercises.
 *
 * The loader therefore reads the whole table. Two screens sharing one page may not disagree about
 * what "the library" is; that is this repo's defining bug class with a noun in it.
 */

/** As much of a `coach_assignments` row as the week needs. */
export type AssignmentRow = {
  readonly workout_id: string | null;
  readonly hidden_from_client: boolean | null;
};

/**
 * The workouts this week actually contains, and how many were withheld.
 *
 * ⚠️ A HIDDEN ASSIGNMENT IS NOT PART OF THE CLIENT'S WEEK AND IS **REPORTED**, never silently
 * dropped. `client.ts` made this rule after measuring two of the owner's twelve assignments
 * hidden — both past and untrained, so every other rule would have called them missed.
 */
export function shownWorkoutIds(assignments: readonly AssignmentRow[]): {
  workoutIds: string[];
  hiddenSkipped: number;
} {
  const workoutIds: string[] = [];
  let hiddenSkipped = 0;
  for (const a of assignments) {
    if (a.hidden_from_client === true) {
      hiddenSkipped += 1;
      continue;
    }
    if (a.workout_id === null) continue;
    if (!workoutIds.includes(a.workout_id)) workoutIds.push(a.workout_id);
  }
  return { workoutIds, hiddenSkipped };
}

export type SetRow = { readonly workout_exercise_id: string | null };

/** How many sets each prescription row carries. `coach_workout_sets` is one row per set. */
export function setsByExerciseRow(sets: readonly SetRow[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of sets) {
    if (s.workout_exercise_id === null) continue;
    out.set(s.workout_exercise_id, (out.get(s.workout_exercise_id) ?? 0) + 1);
  }
  return out;
}

export type ExerciseRow = { readonly id: string; readonly exercise_id: string | null };

/**
 * One week entry per PRESCRIPTION ROW, not per library exercise.
 *
 * ⚠️ THE SAME MOVEMENT LEGITIMATELY APPEARS TWICE IN ONE DAY — a warm-up set of pull-ups and a
 * main set of pull-ups are the same library row — and `training.ts` measured that at 40 of 141
 * (client, date, exercise) triples. `reading` sums per exercise id, so emitting one entry per row
 * is what makes both sets count; collapsing here would silently halve a cell.
 *
 * ⚠️ A ROW WITH NO SETS IS OMITTED RATHER THAN COUNTED AS ZERO. Zero-set rows exist (the player
 * seeds a prescription the moment a workout is opened), and an entry carrying zero sets would add
 * nothing to any cell while making the exercise count as *described* in the completeness line —
 * an honesty column reporting more coverage than the week has.
 */
export function weekEntries(
  exercises: readonly ExerciseRow[],
  setsPerRow: ReadonlyMap<string, number>,
  nameById: ReadonlyMap<string, string>,
): WeekEntry[] {
  const out: WeekEntry[] = [];
  for (const e of exercises) {
    if (e.exercise_id === null) continue;
    const sets = setsPerRow.get(e.id) ?? 0;
    if (sets === 0) continue;
    out.push({ exerciseId: e.exercise_id, name: nameById.get(e.exercise_id) ?? "Unnamed", sets });
  }
  return out;
}

export type CoverageRowRaw = {
  readonly coach_exercise_id: string | null;
  readonly joint_action: string;
  readonly zone: string;
  readonly role: string;
  readonly note: string | null;
};

/**
 * `exercise_coverage`, as the rules take it.
 *
 * ⚠️ A ROW WITHOUT A `coach_exercise_id` IS DROPPED, AND THE COUNT IS RETURNED SO THE CALLER CAN
 * SAY SO. The table has an `exercise_id` column for the engine's own 471-row catalogue and it is
 * populated on ZERO rows today (measured 2026-08-30); the day it is not, a screen keyed on the
 * coach column would quietly describe half the library. A dropped row that nobody counts is how
 * an audit set gets smaller for a reason nobody can see.
 */
export function coverageRows(raw: readonly CoverageRowRaw[]): {
  rows: CoverageRow[];
  skippedWithoutCoachExercise: number;
} {
  const rows: CoverageRow[] = [];
  let skipped = 0;
  for (const r of raw) {
    if (r.coach_exercise_id === null) {
      skipped += 1;
      continue;
    }
    rows.push({
      exerciseId: r.coach_exercise_id,
      action: r.joint_action,
      zone: r.zone as Zone,
      role: r.role as CoverageRow["role"],
      note: r.note ?? "",
    });
  }
  return { rows, skippedWithoutCoachExercise: skipped };
}

/* ── 🔴 F15-2 (W72 step 4, 2026-09-25) — A COACH ROW RESOLVES THROUGH ITS CATALOGUE LINK ─────────
 *
 * MEASURED: this reader keyed on `coach_exercise_id` ALONE, so a coach's row with no hand-authored
 * coverage was "not yet known" even when it IS a catalogue row the coverage pass described. The links
 * were written live on 2026-09-25 (`coach_exercises.catalog_exercise_id`, 170 of 331 rows,
 * `tools/f15-catalog-link-20260922/links-20260925/`). The rule, the SAME on the phone
 * (`CoverageResolution.swift`, its twin — three fixtures each):
 *   1. a coach row with coach-keyed rows reads THOSE — the coach's own description WINS;
 *   2. otherwise, a coach row with a `catalog_exercise_id` reads that catalogue exercise's rows
 *      (keyed `exercise_id`), RE-KEYED to the coach row so every downstream rule is unchanged;
 *   3. otherwise it is undescribed, as before.
 * ⚠️ RESOLVED AT READ TIME, NEVER COPIED (the column comment's rule): nothing writes a catalogue row's
 * cells into the coach's library. ⚠️ The catalogue half is fetched `.in("exercise_id", linkedIds)`,
 * never unfiltered — 3,008 catalogue-keyed rows sit above PostgREST's 1,000-row cap and truncate in
 * silence. */

export type CoachExerciseLink = {
  readonly id: string;
  readonly catalog_exercise_id: string | null;
};

export type CatalogueCoverageRaw = {
  readonly exercise_id: string | null;
  readonly joint_action: string;
  readonly zone: string;
  readonly role: string;
  readonly note: string | null;
};

export type CoverageBasis = "coachKeyed" | "linked";

/** The ids the catalogue half must be fetched for: links of coach rows with no rows of their own. */
export function linkedIdsToFetch(
  coachKeyed: readonly CoverageRowRaw[],
  links: readonly CoachExerciseLink[],
): string[] {
  const own = new Set(coachKeyed.map((r) => r.coach_exercise_id).filter((x): x is string => x !== null));
  const out = new Set<string>();
  for (const l of links) {
    if (l.catalog_exercise_id !== null && !own.has(l.id)) out.add(l.catalog_exercise_id);
  }
  return [...out].sort();
}

/** THE ONE RESOLVER. `rows` is what the matrix takes; `basis` says, per coach row, which rule described it. */
export function coverageRowsFor(
  coachKeyed: readonly CoverageRowRaw[],
  catalogue: readonly CatalogueCoverageRaw[],
  links: readonly CoachExerciseLink[],
): { rows: CoverageRow[]; basis: Map<string, CoverageBasis>; skippedWithoutCoachExercise: number } {
  const { rows, skippedWithoutCoachExercise } = coverageRows(coachKeyed);
  const basis = new Map<string, CoverageBasis>();
  for (const r of rows) basis.set(r.exerciseId, "coachKeyed");
  const byCatalogue = new Map<string, CatalogueCoverageRaw[]>();
  for (const c of catalogue) {
    if (c.exercise_id === null) continue;
    byCatalogue.set(c.exercise_id, [...(byCatalogue.get(c.exercise_id) ?? []), c]);
  }
  const out = [...rows];
  for (const l of links) {
    if (basis.has(l.id) || l.catalog_exercise_id === null) continue;
    const cells = byCatalogue.get(l.catalog_exercise_id);
    if (cells === undefined || cells.length === 0) continue;
    basis.set(l.id, "linked");
    for (const c of cells) {
      out.push({
        exerciseId: l.id,
        action: c.joint_action,
        zone: c.zone as Zone,
        role: c.role as CoverageRow["role"],
        note: c.note ?? "",
      });
    }
  }
  return { rows: out, basis, skippedWithoutCoachExercise };
}
